// Seamless Windows Hook for Baba AI
// Monitors token count and triggers automatic rollover at 95K tokens
//
// WHAT IT DOES:
// 1. Monitors token count after each message
// 2. When 95K reached: archives current chat to GDrive
// 3. Creates new chat with essential context transferred
// 4. User sees ONE continuous conversation
//
// ARCHITECTURE:
// - Uses tokenService for counting
// - Uses googleDriveService for archiving
// - Returns status for optional UI display
// - Handles rollover transparently

import { useState, useCallback, useRef } from 'react';
import tokenService from '../services/tokenService';
import googleDriveService from '../services/googleDriveService';

/**
 * Hook for seamless window management
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.onRollover - Callback when rollover occurs
 * @param {Function} options.createNewChat - Function to create new chat
 * @param {Function} options.getMessages - Function to get current messages
 * @param {Function} options.setMessages - Function to set messages
 * @param {string} options.chatTitle - Current chat title
 * @returns {Object} Hook state and methods
 */
export function useSeamlessWindows(options = {}) {
  const {
    onRollover,
    createNewChat,
    getMessages,
    setMessages,
    chatTitle = 'Chat'
  } = options;

  // State
  const [tokenStatus, setTokenStatus] = useState({
    currentTokens: 0,
    percentage: 0,
    status: 'healthy',
    shouldRollover: false
  });
  const [isRollingOver, setIsRollingOver] = useState(false);
  const [rolloverError, setRolloverError] = useState(null);
  const [lastRollover, setLastRollover] = useState(null);

  // Ref to prevent multiple simultaneous rollovers
  const rolloverInProgress = useRef(false);

  /**
   * Update token count based on current messages
   */
  const updateTokenCount = useCallback((messages) => {
    const status = tokenService.checkThreshold(messages);
    setTokenStatus(status);
    return status;
  }, []);

  /**
   * Check if rollover is needed and execute if so
   * Call this after every message update
   */
  const checkAndRollover = useCallback(async (messages, currentChatId) => {
    // Update token count
    const status = updateTokenCount(messages);
    
    // Check if rollover needed
    if (!status.shouldRollover) {
      return { rolled: false, status };
    }

    // Prevent multiple simultaneous rollovers
    if (rolloverInProgress.current) {
      console.log('[SeamlessWindows] Rollover already in progress, skipping');
      return { rolled: false, status, reason: 'in_progress' };
    }

    // Check if GDrive is authenticated
    if (!googleDriveService.isAuthenticated()) {
      console.warn('[SeamlessWindows] GDrive not authenticated, cannot archive');
      setRolloverError('Google Drive not connected. Please authenticate in Settings.');
      return { rolled: false, status, reason: 'no_gdrive' };
    }

    // Execute rollover
    rolloverInProgress.current = true;
    setIsRollingOver(true);
    setRolloverError(null);

    try {
      console.log('[SeamlessWindows] Starting rollover...', {
        tokens: status.currentTokens,
        messages: messages.length
      });

      // Step 1: Create archive data
      const archiveData = createArchiveData(messages, currentChatId, chatTitle);
      
      // Step 2: Archive to GDrive
      const archiveResult = await googleDriveService.archiveChat(
        archiveData,
        chatTitle
      );
      console.log('[SeamlessWindows] Chat archived:', archiveResult);

      // Step 3: Extract context to carry forward
      const contextToTransfer = extractTransferContext(messages);

      // Step 4: Create new chat (via callback)
      let newChatId = null;
      if (createNewChat) {
        newChatId = createNewChat();
      }

      // Step 5: Inject context into new chat (via callback)
      if (setMessages && contextToTransfer.length > 0) {
        setMessages(contextToTransfer);
      }

      // Step 6: Record rollover
      const rolloverRecord = {
        timestamp: new Date().toISOString(),
        archivedChatId: currentChatId,
        newChatId,
        archiveFileId: archiveResult?.id,
        tokensArchived: status.currentTokens,
        messagesArchived: messages.length
      };
      setLastRollover(rolloverRecord);

      // Step 7: Callback
      if (onRollover) {
        onRollover(rolloverRecord);
      }

      console.log('[SeamlessWindows] Rollover complete:', rolloverRecord);

      return { 
        rolled: true, 
        status, 
        rolloverRecord 
      };

    } catch (error) {
      console.error('[SeamlessWindows] Rollover failed:', error);
      setRolloverError(error.message);
      return { 
        rolled: false, 
        status, 
        reason: 'error',
        error: error.message 
      };
    } finally {
      setIsRollingOver(false);
      rolloverInProgress.current = false;
    }
  }, [chatTitle, createNewChat, onRollover, setMessages, updateTokenCount]);

  /**
   * Manual rollover trigger (for testing or user-initiated)
   */
  const forceRollover = useCallback(async (messages, currentChatId) => {
    // Temporarily lower threshold to force rollover
    const originalCheck = tokenService.checkThreshold;
    tokenService.checkThreshold = () => ({ 
      ...originalCheck(messages), 
      shouldRollover: true 
    });
    
    const result = await checkAndRollover(messages, currentChatId);
    
    // Restore original
    tokenService.checkThreshold = originalCheck;
    
    return result;
  }, [checkAndRollover]);

  return {
    // State
    tokenStatus,
    isRollingOver,
    rolloverError,
    lastRollover,
    
    // Methods
    updateTokenCount,
    checkAndRollover,
    forceRollover,
    
    // Convenience getters
    isHealthy: tokenStatus.status === 'healthy',
    isWarning: ['warning', 'critical', 'exceeded'].includes(tokenStatus.status),
    formattedTokens: tokenService.formatTokenCount(tokenStatus.currentTokens)
  };
}

/**
 * Create archive data structure for GDrive storage
 */
function createArchiveData(messages, chatId, chatTitle) {
  return {
    metadata: {
      chatId,
      title: chatTitle,
      archivedAt: new Date().toISOString(),
      tokenCount: tokenService.countConversationTokens(messages),
      messageCount: messages.length,
      firstMessageAt: messages[0]?.timestamp || null,
      lastMessageAt: messages[messages.length - 1]?.timestamp || null
    },
    messages: messages.map(msg => ({
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp,
      // Include file references but not full content (too large)
      files: msg.files?.map(f => ({
        name: f.name,
        type: f.type,
        size: f.size
        // content excluded - too large for archive
      }))
    })),
    // Full messages preserved for complete access
    fullMessages: messages
  };
}

/**
 * Extract essential context to transfer to new chat
 * This is what makes the rollover "seamless"
 */
function extractTransferContext(messages) {
  // Strategy: Keep last N messages that fit within ~10K tokens
  // Plus extract any "important" markers
  
  const MAX_TRANSFER_TOKENS = 10000;
  const transferMessages = [];
  let tokenCount = 0;
  
  // Work backwards from most recent
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    const msgTokens = tokenService.countMessageTokens(msg);
    
    if (tokenCount + msgTokens > MAX_TRANSFER_TOKENS) {
      break;
    }
    
    transferMessages.unshift(msg);
    tokenCount += msgTokens;
  }
  
  // Add context header as system-like message
  if (transferMessages.length > 0) {
    const contextHeader = {
      role: 'assistant',
      content: `[Context: This conversation continues from a previous session. ${messages.length - transferMessages.length} earlier messages were archived to Google Drive. The conversation continues seamlessly below.]`,
      timestamp: new Date().toISOString(),
      isContextHeader: true
    };
    transferMessages.unshift(contextHeader);
  }
  
  return transferMessages;
}

export default useSeamlessWindows;
